import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import type { ReactNode } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { usePostHog } from "posthog-js/react";

import {
  useArrangementCatalog,
  useArrangementConfig,
  useArrangementPricing,
  useArrangementSelections as useArrangementSavedSelections,
  useCalculateQuote,
  useCasePurchaser,
  useCaseViewer,
  useSaveArrangementSelections,
} from "hooks/useArrangementAPI";
import { useDebouncedCallback } from "hooks/useDebouncedCallback";
import { isArrangementLocked } from "helpers/at-need";

import { hasArrangementAccessDeniedError } from "./access-denied";
import type {
  ArrangementState,
  ArrangementStateContextType,
  ArrangementActionsContextType,
  ArrangementContextType,
  ArrangementConfig,
  CaseContact,
  QuoteBreakdown,
  AdditionalUrnSelection,
  EngravingOptions,
  ContactInfo,
  FulfillmentDetails,
  ShippingAddress,
  ServiceAnswer,
  KeepsakeSelection,
  AddOnAnswer,
  SelectionsSaveResponse,
  ArrangementFlowError,
} from "./types";
import { STEP_ROUTES } from "./constants";
import type { PackageSection } from "./screenModel";
import {
  chargeableServiceAnswers,
  computeServiceAnswerCost,
  mapSelectionsToQuoteInput,
  getIncludedOfferingQuantity,
  getRushCremationVariantId,
  isFingerprintPreservationIncluded,
  isClientSideQuestionId,
  QUESTION_ID,
} from "./helpers";
import {
  deserializeArrangementSelections,
  serializeArrangementSelections,
} from "./selections-serialization";
import { ENGRAVING_PRICE } from "./engravingConstants";

const EMPTY_SHIPPING_ADDRESS: ArrangementState["fulfillment"]["shippingAddress"] =
  {
    recipientName: "",
    street: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
  };

const initialState: ArrangementState = {
  currentStep: 0,
  selectedPackageId: null,
  includedUrnId: null,
  includedUrnVariantId: null,
  includedUrnEngraving: null,
  additionalUrns: [],
  selectedKeepsakes: [],
  contactInfo: {
    name: "",
    isPrimaryContact: null,
    alternateContactName: "",
    alternateContactPhone: "",
    alternateContactEmail: "",
    otherNextOfKin: "",
  },
  fulfillment: {
    dispositionMethod: "",
    shippingAddress: EMPTY_SHIPPING_ADDRESS,
    specialRequests: "",
    noUnitConfirmed: false,
  },
  serviceAnswers: [],
  addOnSelections: [],
  isDeceased: null,
  quoteEmails: [""],
  paymentMethodId: null,
};

/**
 * Write answers into a state, keeping one entry per question with the written
 * one last. Every answer write goes through here: a second copy of this merge
 * would sooner or later stop clearing the shipping address below.
 */
const mergeServiceAnswers = (
  state: ArrangementState,
  answers: ServiceAnswer[],
): ArrangementState => {
  const writtenIds = new Set(answers.map((a) => a.questionId));
  const next: ArrangementState = {
    ...state,
    serviceAnswers: [
      ...state.serviceAnswers.filter((a) => !writtenIds.has(a.questionId)),
      ...answers,
    ],
  };
  // Clear shipping address when switching delivery to pickup
  if (
    answers.some(
      (a) =>
        a.questionId === QUESTION_ID.DELIVERY &&
        a.selectedOptionId === "pickup",
    )
  ) {
    next.fulfillment = {
      ...state.fulfillment,
      shippingAddress: EMPTY_SHIPPING_ADDRESS,
      noUnitConfirmed: false,
    };
  }
  return next;
};

// B1: Two separate contexts
const ArrangementStateContext = createContext<
  ArrangementStateContextType | undefined
>(undefined);

const ArrangementActionsContext = createContext<
  ArrangementActionsContextType | undefined
>(undefined);

export const ArrangementProvider = ({
  children,
  isCareSpecialist = false,
}: {
  children: ReactNode;
  isCareSpecialist?: boolean;
}) => {
  const { caseId } = useParams<{ caseId: string }>();
  const [state, setState] = useState<ArrangementState>(initialState);
  const [hasInitialized, setHasInitialized] = useState(false);
  const navigate = useNavigate();
  const posthog = usePostHog();
  const location = useLocation();
  const searchParams = useRef(window.location.search);

  // API hooks
  const {
    data: configData,
    error: configError,
    isLoading: configLoading,
  } = useArrangementConfig(caseId);
  const {
    data: pricingData,
    error: pricingError,
    isLoading: pricingLoading,
  } = useArrangementPricing(caseId);
  const {
    data: catalogData,
    error: catalogError,
    isLoading: catalogLoading,
  } = useArrangementCatalog(caseId);
  const {
    data: savedState,
    error: stateError,
    isLoading: stateLoading,
  } = useArrangementSavedSelections(caseId);
  const { trigger: saveSelections, isMutating: isSaving } =
    useSaveArrangementSelections(caseId);
  const { trigger: calculateQuote, isMutating: isQuoteLoading } =
    useCalculateQuote(caseId);
  const {
    data: viewerData,
    error: viewerError,
    isLoading: viewerLoading,
  } = useCaseViewer(caseId);
  const {
    data: purchaserData,
    error: purchaserError,
    isLoading: purchaserLoading,
  } = useCasePurchaser(caseId);

  const viewer = (viewerData as CaseContact | null) ?? null;
  const purchaser = (purchaserData as CaseContact | null) ?? null;
  const isViewerPurchaser =
    !!viewer?.person &&
    !!purchaser?.person &&
    viewer.person === purchaser.person;

  const [quoteBreakdown, setQuoteBreakdown] = useState<QuoteBreakdown | null>(
    null,
  );

  // Completion status published by step components as they mount. The progress
  // bar reads it without owning per-step completion checks. Reports persist, so
  // a completed step stays checked from any later step.
  const [stepStatus, setStepStatus] = useState<Record<number, boolean>>({});
  const reportStepStatus = useCallback((step: number, complete: boolean) => {
    setStepStatus((prev) =>
      prev[step] === complete ? prev : { ...prev, [step]: complete },
    );
  }, []);

  // Compose the legacy ArrangementConfig shape from the now-split endpoints
  // so the rest of the flow keeps reading `config.*` unchanged during the
  // transition. /config (slim) → case_context + the death-certificate ordering
  // copy for this case's county; /pricing → packages + add-ons
  // + service questions + inclusions; /catalog → urns + keepsakes. The
  // arrangement status comes from /selections (savedState) — /config no longer
  // returns it.
  const config = useMemo<ArrangementConfig | null>(() => {
    if (!configData || !pricingData || !catalogData) return null;
    const slim = configData as Pick<
      ArrangementConfig,
      | "case_context"
      | "dc_ordering_template"
      | "dc_ordering_template_key"
      | "dc_timeline_family_orders"
      | "dc_timeline_after_orders"
    >;
    const pricing = pricingData as Pick<
      ArrangementConfig,
      | "packages"
      | "add_ons"
      | "add_on_groups"
      | "service_questions"
      | "package_inclusions"
    >;
    const catalog = catalogData as Pick<
      ArrangementConfig,
      "urns" | "keepsakes"
    >;
    const arrangementStatus =
      (savedState as { arrangement?: { status?: string | null } } | undefined)
        ?.arrangement?.status ?? null;
    return {
      packages: pricing.packages,
      add_ons: pricing.add_ons,
      add_on_groups: pricing.add_on_groups,
      service_questions: pricing.service_questions,
      package_inclusions: pricing.package_inclusions,
      urns: catalog.urns,
      keepsakes: catalog.keepsakes,
      case_context: slim.case_context,
      // Absent until the server populates them, which is what the screen's own
      // fallbacks are for.
      dc_ordering_template: slim.dc_ordering_template,
      dc_ordering_template_key: slim.dc_ordering_template_key,
      dc_timeline_family_orders: slim.dc_timeline_family_orders,
      dc_timeline_after_orders: slim.dc_timeline_after_orders,
      arrangement_status: arrangementStatus,
    };
  }, [configData, pricingData, catalogData, savedState]);

  const isLoading =
    configLoading ||
    pricingLoading ||
    catalogLoading ||
    stateLoading ||
    viewerLoading ||
    purchaserLoading;
  // A 404 from /purchaser means "no purchaser assigned to the case yet" — a
  // valid empty state, not a flow-breaking error. Any other purchaser error
  // (and any viewer error) is fatal and should surface the error screen.
  const purchaserStatusCode = (
    purchaserError as { statusCode?: number } | undefined
  )?.statusCode;
  const fatalPurchaserError =
    purchaserError && purchaserStatusCode !== 404 ? purchaserError : null;
  const error = useMemo<ArrangementFlowError | null>(() => {
    if (
      hasArrangementAccessDeniedError({
        configError,
        pricingError,
        catalogError,
        stateError,
      })
    ) {
      return Object.assign(new Error("Arrangement access denied"), {
        type: "access_denied" as const,
        statusCode: 403,
      });
    }

    // Keep the original SWR error object so callers can still read backend
    // fields such as `statusCode` off normal, non-access-denied errors.
    return (
      ((configError ??
        pricingError ??
        catalogError ??
        stateError ??
        viewerError ??
        fatalPurchaserError) as ArrangementFlowError | undefined) ?? null
    );
  }, [
    configError,
    pricingError,
    catalogError,
    stateError,
    viewerError,
    fatalPurchaserError,
  ]);

  // B2: Stable refs for state/config access inside callbacks
  const stateRef = useRef(state);
  stateRef.current = state;
  const configRef = useRef(config);
  configRef.current = config;

  // B4: Refs for unstable values used in effects
  const posthogRef = useRef(posthog);
  posthogRef.current = posthog;
  const saveSelectionsRef = useRef(saveSelections);
  saveSelectionsRef.current = saveSelections;
  const calculateQuoteRef = useRef(calculateQuote);
  calculateQuoteRef.current = calculateQuote;

  // B7: Unmount guard for async
  // Reset to true on (re)mount so React 18 StrictMode double-mount cycle
  // doesn't permanently mark the ref as false.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // B3: Track last saved state to skip no-op saves
  const lastSavedRef = useRef<string>("");

  // Merge persisted selections on initial load. /selections persists the strict
  // snake_case allowlist (selections, engraving, and fulfillment). contactInfo,
  // isDeceased, quoteEmails, and paymentMethodId are NOT persisted — they live
  // in React state only.
  useEffect(() => {
    if (
      hasInitialized ||
      isLoading ||
      configError ||
      stateError ||
      pricingError ||
      catalogError ||
      viewerError ||
      fatalPurchaserError
    )
      return;
    if (savedState && typeof savedState === "object") {
      const wrapper = savedState as {
        arrangement?: Parameters<typeof deserializeArrangementSelections>[0];
      };
      const persisted = deserializeArrangementSelections(wrapper.arrangement);
      const cfg = configRef.current;
      // Rush cremation is priced from its service answer, never as an add-on
      // selection; strip any stored one so it cannot double-price with the
      // quote's rush boolean (its card is hidden, so it would be uneditable).
      const rushVariantId = getRushCremationVariantId(cfg);
      if (persisted.addOnSelections && rushVariantId) {
        persisted.addOnSelections = persisted.addOnSelections.filter(
          (a) => a.serviceId !== rushVariantId,
        );
      }
      // Drop hydrated selections/answers the config no longer serves (e.g. an
      // offering unflagged after the draft was saved) — otherwise they keep
      // silently pricing in the server quote. Editable drafts only: a
      // read-only (Care Specialist) or locked (submitted/completed)
      // arrangement must render exactly what was saved.
      if (
        cfg &&
        !isCareSpecialist &&
        !isArrangementLocked(cfg.arrangement_status)
      ) {
        if (persisted.addOnSelections) {
          const addOnIds = new Set(cfg.add_ons.map((a) => a.id));
          persisted.addOnSelections = persisted.addOnSelections.filter((a) =>
            addOnIds.has(a.serviceId),
          );
        }
        if (persisted.serviceAnswers) {
          const questionIds = new Set(cfg.service_questions.map((q) => q.id));
          persisted.serviceAnswers = persisted.serviceAnswers.filter(
            (a) =>
              questionIds.has(a.questionId) ||
              isClientSideQuestionId(a.questionId),
          );
        }
      }
      if (Object.keys(persisted).length > 0) {
        setState((prev) => ({ ...prev, ...persisted }));
      }
    }
    setHasInitialized(true);
    posthogRef.current?.capture("arrangement_flow_started", {
      case_id: caseId,
    });
    const marketSlug = configRef.current?.case_context?.market_slug;
    if (marketSlug) {
      posthogRef.current?.setPersonProperties({
        latest_case_market_slug: marketSlug.toLowerCase(),
      });
    }
  }, [
    isLoading,
    savedState,
    hasInitialized,
    caseId,
    isCareSpecialist,
    configError,
    stateError,
    pricingError,
    catalogError,
    viewerError,
    fatalPurchaserError,
  ]);

  // B6: Debounced auto-save using extracted hook + B3: skip no-op saves
  // The PUT on the wire, kept with the payload it is carrying. Distinct from
  // `lastSavedRef`, which only records what the server has taken: a flush and
  // the autosave the same handler schedules would otherwise both send the
  // identical body whenever the round-trip outlasts the debounce. Keeping the
  // promise beside the payload is what lets the second of them wait on the
  // first instead of issuing a duplicate PUT.
  const inFlightSaveRef = useRef<{
    serialized: string;
    promise: Promise<void>;
  } | null>(null);

  // Selections the server refused to keep, dropped from the draft so the screen
  // can't go on showing a purchase the backend never recorded.
  const applySelectionsResponse = useCallback((response: unknown) => {
    if (!mountedRef.current) return;
    const { stripped } = (response ?? {}) as SelectionsSaveResponse;

    if (!stripped?.length) return;

    setState((prev) => {
      let next = prev;

      for (const item of stripped) {
        if (item.type === "included_urn") {
          next = {
            ...next,
            includedUrnId: null,
            includedUrnVariantId: null,
          };
        } else if (item.type === "additional_urn") {
          next = {
            ...next,
            additionalUrns: next.additionalUrns.filter(
              (u) => u.urnId !== item.itemId,
            ),
          };
        } else if (item.type === "keepsake") {
          next = {
            ...next,
            selectedKeepsakes: next.selectedKeepsakes.filter(
              (k) => k.productId !== item.itemId,
            ),
          };
        }
      }

      return next;
    });
  }, []);

  // The one place a selections PUT is issued, so the debounced autosave and the
  // manual flush share a single record of what is in flight and what the server
  // has taken. Null where nothing was sent at all.
  const persistSelections = useCallback(
    (persistableState: ArrangementState): Promise<void> | null => {
      // Backend also rejects terminal writes with 409; catching here avoids
      // the roundtrip. Null/draft remain editable (new customers start null).
      if (isArrangementLocked(configRef.current?.arrangement_status)) {
        return null;
      }
      const wirePayload = serializeArrangementSelections(persistableState);
      const serialized = JSON.stringify(wirePayload);
      // Nothing of its own to send: whichever save already carries this exact
      // payload speaks for this caller too, and waiting on it is what lets a
      // flush refuse to advance on a draft that never landed.
      const inFlight = inFlightSaveRef.current;
      if (inFlight?.serialized === serialized) return inFlight.promise;
      if (serialized === lastSavedRef.current) return null;

      const savePromise = saveSelectionsRef
        .current(wirePayload)
        .then((response) => {
          // The payload the server was actually sent, recorded only once it has
          // it — a failed save has to stay unsaved so the next autosave carries
          // it again — and never whatever the draft holds by now, since an edit
          // made while this request was in flight would then be recorded as
          // saved though the server never received it, and skipped as a no-op
          // ever after.
          lastSavedRef.current = serialized;
          applySelectionsResponse(response);
        })
        .finally(() => {
          // A failed save leaves nothing to adopt: its payload is unsaved, so
          // the next one has to carry it again rather than wait on this.
          if (inFlightSaveRef.current?.serialized === serialized) {
            inFlightSaveRef.current = null;
          }
        });
      inFlightSaveRef.current = { serialized, promise: savePromise };
      return savePromise;
    },
    [applySelectionsResponse],
  );

  const debouncedSave = useDebouncedCallback(
    (persistableState: ArrangementState) => {
      persistSelections(persistableState)?.catch(() => {
        // Save failures are non-blocking; user can retry on next interaction
      });
    },
    500,
  );

  useEffect(() => {
    if (
      !hasInitialized ||
      !caseId ||
      isCareSpecialist ||
      configError ||
      stateError ||
      pricingError ||
      catalogError ||
      viewerError ||
      fatalPurchaserError
    )
      return;
    if (isArrangementLocked(config?.arrangement_status)) return;
    debouncedSave(state);
  }, [
    state,
    hasInitialized,
    caseId,
    isCareSpecialist,
    debouncedSave,
    configError,
    stateError,
    pricingError,
    catalogError,
    viewerError,
    fatalPurchaserError,
    config?.arrangement_status,
  ]);

  // B5: Memoize the quote input from individual fields
  const additionalUrnCount = useMemo(
    () => state.additionalUrns.reduce((sum, u) => sum + u.quantity, 0),
    [state.additionalUrns],
  );

  const fillableKeepsakeCount = useMemo(
    () =>
      state.selectedKeepsakes.reduce((sum, sel) => {
        const catalogFillable =
          config?.keepsakes.find((k) => k.id === sel.productId)?.fillable ===
          true;
        const isFillable = sel.fillable ?? catalogFillable;
        return sum + (isFillable ? sel.quantity : 0);
      }, 0),
    [config?.keepsakes, state.selectedKeepsakes],
  );

  const quoteInput = useMemo(
    () =>
      mapSelectionsToQuoteInput(
        state.selectedPackageId,
        state.serviceAnswers,
        state.addOnSelections,
        !!state.includedUrnId,
        additionalUrnCount,
        fillableKeepsakeCount,
      ),
    [
      state.selectedPackageId,
      state.serviceAnswers,
      state.addOnSelections,
      state.includedUrnId,
      additionalUrnCount,
      fillableKeepsakeCount,
    ],
  );

  // B6: Debounced quote recalculation using extracted hook
  const prevQuoteInputRef = useRef<string>("");
  const quoteRequestIdRef = useRef(0);

  const debouncedQuote = useDebouncedCallback(
    (input: NonNullable<typeof quoteInput>) => {
      const requestId = ++quoteRequestIdRef.current;
      calculateQuoteRef
        .current(input)
        .then((result) => {
          if (!mountedRef.current) return;
          if (requestId !== quoteRequestIdRef.current) return;
          if (result && typeof result === "object") {
            setQuoteBreakdown(result as QuoteBreakdown);
          }
        })
        .catch(() => {
          if (!mountedRef.current) return;
          if (requestId !== quoteRequestIdRef.current) return;
          // Clear stale breakdown so UI falls back to computedTotal
          setQuoteBreakdown(null);
        });
    },
    500,
  );

  useEffect(() => {
    if (!hasInitialized || !caseId || !quoteInput) return;

    const serialized = JSON.stringify(quoteInput);
    if (serialized === prevQuoteInputRef.current) return;
    prevQuoteInputRef.current = serialized;

    debouncedQuote(quoteInput);
  }, [quoteInput, hasInitialized, caseId, debouncedQuote]);

  const currentStep = useMemo(() => {
    const path = location.pathname.split("/").pop() || "";
    const idx = STEP_ROUTES.indexOf(path as (typeof STEP_ROUTES)[number]);
    return idx >= 0 ? idx : 0;
  }, [location.pathname]);

  const packageInclusions = useMemo(() => {
    if (!config) return null;
    return config.package_inclusions[state.selectedPackageId || ""] ?? null;
  }, [config, state.selectedPackageId]);

  // Selections the package decides rather than the family. Enforced here, not
  // in the subviews that render them, because those only mount when their
  // section is on screen: a case restored straight onto /services or
  // /confirmation would otherwise reach the end with selections its package
  // contradicts. Editable drafts only — a read-only or locked arrangement must
  // render exactly what was saved.
  useEffect(() => {
    if (!hasInitialized || !config || isCareSpecialist) return;
    if (isArrangementLocked(config.arrangement_status)) return;
    if (!packageInclusions) return;

    const eligibleUrnIds = packageInclusions.eligibleUrnIds ?? [];
    const fingerprintsIncluded = isFingerprintPreservationIncluded(
      config,
      packageInclusions,
    );

    setState((prev) => {
      let next = prev;

      // An included urn the package no longer covers is dropped, and a package
      // offering exactly one has it selected outright — there is nothing to
      // choose, and no Remove action to recover from an empty selection.
      if (eligibleUrnIds.length > 0) {
        const savedIsEligible =
          !!prev.includedUrnId && eligibleUrnIds.includes(prev.includedUrnId);
        if (prev.includedUrnId && !savedIsEligible) {
          next = {
            ...next,
            includedUrnId: null,
            includedUrnVariantId: null,
            includedUrnEngraving: null,
          };
        }
        if (!savedIsEligible && eligibleUrnIds.length === 1) {
          const urn = config.urns.find((u) => u.id === eligibleUrnIds[0]);
          if (urn) {
            next = {
              ...next,
              includedUrnId: urn.id,
              includedUrnVariantId: urn.variants[0]?.id || "",
              includedUrnEngraving: null,
            };
          }
        }
      }

      // Where the package covers preservation, "yes" is the only answer
      // (ENG-1149). A package change clears this answer first, so a "yes" the
      // family never chose can't survive onto a plan that would bill for it.
      if (fingerprintsIncluded) {
        const answer = next.serviceAnswers.find(
          (a) => a.questionId === QUESTION_ID.FINGERPRINTS,
        );
        if (answer?.selectedOptionId !== "yes") {
          next = {
            ...next,
            serviceAnswers: [
              ...next.serviceAnswers.filter(
                (a) => a.questionId !== QUESTION_ID.FINGERPRINTS,
              ),
              {
                questionId: QUESTION_ID.FINGERPRINTS,
                selectedOptionId: "yes",
              },
            ],
          };
        }
      }

      return next;
    });
  }, [hasInitialized, config, isCareSpecialist, packageInclusions]);

  // B2: Stable callbacks via refs — config and state accessed via refs
  const setSelectedPackage = useCallback(
    (id: string) => {
      const cfg = configRef.current;
      const pkg = cfg?.packages.find((p) => p.variant_id === id);
      posthogRef.current?.capture("arrangement_package_selected", {
        case_id: caseId,
        package_id: id,
        package_title: pkg?.title,
      });

      const inclusions = cfg?.package_inclusions[id];
      const eligible = inclusions?.eligibleUrnIds ?? [];

      // Clear package-derived service answers so ServicesStep re-applies
      // defaults based on the new package's inclusions. Fingerprints belongs
      // here because the answer can be set for the family when the package
      // covers preservation: left in place, that "yes" would carry onto a plan
      // that bills for it. Cleared, it is either re-forced or asked again.
      const PACKAGE_DERIVED_QUESTIONS = [
        "q-delivery",
        "q-death-certs",
        "q-fingerprints",
      ];
      const clearDerivedAnswers = (answers: ServiceAnswer[]) =>
        answers.filter(
          (a) => !PACKAGE_DERIVED_QUESTIONS.includes(a.questionId),
        );

      // Fingerprint jewelry can't be made without the preservation. Clearing
      // the answer above would otherwise leave those pieces in the cart,
      // priced and uneditable (their controls gate on a "yes"), while the
      // quote sends collect_fingerprints: false — the same unfulfillable state
      // the "No thanks" flow removes them to avoid. Only on a plan that
      // doesn't cover preservation: where it does, the answer is re-forced to
      // "yes" and the pieces stay valid. Additional urns already reset here
      // for the same reason.
      const fingerprintProductIds = new Set(
        (cfg?.keepsakes ?? []).filter((k) => k.fingerprint).map((k) => k.id),
      );
      const keepsFingerprints =
        !!cfg && isFingerprintPreservationIncluded(cfg, inclusions ?? null);
      const reconcileKeepsakes = (keepsakes: KeepsakeSelection[]) =>
        keepsFingerprints || fingerprintProductIds.size === 0
          ? keepsakes
          : keepsakes.filter((k) => !fingerprintProductIds.has(k.productId));

      if (eligible.length === 1) {
        const urn = cfg?.urns.find((u) => u.id === eligible[0]);
        const variantId = urn?.variants[0]?.id || "";
        setState((prev) => ({
          ...prev,
          selectedPackageId: id,
          includedUrnId: eligible[0],
          includedUrnVariantId: variantId,
          additionalUrns: [],
          selectedKeepsakes: reconcileKeepsakes(prev.selectedKeepsakes),
          serviceAnswers: clearDerivedAnswers(prev.serviceAnswers),
        }));
      } else {
        setState((prev) => ({
          ...prev,
          selectedPackageId: id,
          includedUrnId: null,
          includedUrnVariantId: null,
          additionalUrns: [],
          selectedKeepsakes: reconcileKeepsakes(prev.selectedKeepsakes),
          serviceAnswers: clearDerivedAnswers(prev.serviceAnswers),
        }));
      }
    },
    [caseId],
  );

  const setIncludedUrn = useCallback((urnId: string, variantId: string) => {
    setState((prev) => ({
      ...prev,
      includedUrnId: urnId,
      includedUrnVariantId: variantId,
    }));
  }, []);

  const clearIncludedUrn = useCallback(() => {
    setState((prev) => ({
      ...prev,
      includedUrnId: null,
      includedUrnVariantId: null,
      includedUrnEngraving: null,
    }));
  }, []);

  const setIncludedUrnEngraving = useCallback(
    (engraving: EngravingOptions | null) => {
      setState((prev) => ({ ...prev, includedUrnEngraving: engraving }));
    },
    [],
  );

  const setAdditionalUrn = useCallback(
    (
      urnId: string,
      variantId: string,
      quantity: number,
      engraving?: EngravingOptions | null,
    ) => {
      if (quantity > 0) {
        const urn = configRef.current?.urns.find((u) => u.id === urnId);
        const variant = urn?.variants.find((v) => v.id === variantId);
        posthogRef.current?.capture("arrangement_additional_urn_selected", {
          urn_id: urnId,
          shopify_product_id: urn?.shopifyProductId,
          name: urn?.name,
          price: variant?.price ?? urn?.price,
          variant_id: variantId,
          quantity,
          cohort_test_urn: !!urn?.cohortTest,
        });
      }
      setState((prev) => {
        const others = prev.additionalUrns.filter((u) => u.urnId !== urnId);
        const updated: AdditionalUrnSelection[] =
          quantity > 0
            ? [...others, { urnId, variantId, quantity, engraving }]
            : others;
        return { ...prev, additionalUrns: updated };
      });
    },
    [],
  );

  const clearAdditionalUrn = useCallback((urnId: string) => {
    setState((prev) => ({
      ...prev,
      additionalUrns: prev.additionalUrns.filter((u) => u.urnId !== urnId),
    }));
  }, []);

  const setKeepsakeSelection = useCallback(
    (productId: string, variantId: string, quantity: number) => {
      if (quantity > 0) {
        const keepsake = configRef.current?.keepsakes.find(
          (k) => k.id === productId,
        );
        const variant = keepsake?.variants?.find((v) => v.id === variantId);
        posthogRef.current?.capture("arrangement_keepsake_selected", {
          keepsake_id: productId,
          shopify_product_id: keepsake?.shopifyProductId,
          name: keepsake?.name,
          price: variant?.price ?? keepsake?.price,
          variant_id: variantId,
          quantity,
          cohort_test_keepsake: !!keepsake?.cohortTest,
        });
      }
      setState((prev) => {
        const keepsake = configRef.current?.keepsakes.find(
          (k) => k.id === productId,
        );
        const existing = prev.selectedKeepsakes.filter(
          (k) => k.productId !== productId,
        );
        const updated: KeepsakeSelection[] =
          quantity > 0
            ? [
                ...existing,
                {
                  productId,
                  variantId,
                  quantity,
                  // Only record fillable once the catalog has resolved the
                  // keepsake; leaving it undefined lets the count memo fall
                  // back to the catalog instead of locking in a stale `false`.
                  ...(keepsake ? { fillable: keepsake.fillable === true } : {}),
                },
              ]
            : existing;
        return { ...prev, selectedKeepsakes: updated };
      });
    },
    [],
  );

  const clearKeepsakeSelection = useCallback((productId: string) => {
    setState((prev) => ({
      ...prev,
      selectedKeepsakes: prev.selectedKeepsakes.filter(
        (k) => k.productId !== productId,
      ),
    }));
  }, []);

  const updateContactInfo = useCallback((info: Partial<ContactInfo>) => {
    setState((prev) => ({
      ...prev,
      contactInfo: { ...prev.contactInfo, ...info },
    }));
  }, []);

  const updateFulfillment = useCallback(
    (fulfillment: Partial<FulfillmentDetails>) => {
      setState((prev) => ({
        ...prev,
        fulfillment: { ...prev.fulfillment, ...fulfillment },
      }));
    },
    [],
  );

  const updateFulfillmentAddress = useCallback(
    (address: Partial<ShippingAddress>) => {
      setState((prev) => ({
        ...prev,
        fulfillment: {
          ...prev.fulfillment,
          shippingAddress: { ...prev.fulfillment.shippingAddress, ...address },
        },
      }));
    },
    [],
  );

  const setServiceAnswer = useCallback((answer: ServiceAnswer) => {
    setState((prev) => mergeServiceAnswers(prev, [answer]));
  }, []);

  const setAddOnSelection = useCallback(
    (serviceId: string, quantity: number) => {
      setState((prev) => {
        const addOns = configRef.current?.add_ons ?? [];
        const groups = configRef.current?.add_on_groups ?? [];
        const service = addOns.find((s) => s.id === serviceId);
        let others = prev.addOnSelections.filter(
          (a) => a.serviceId !== serviceId,
        );
        // Apply mutex only when the group is explicitly marked exclusive
        // (e.g. viewing-witness). Non-exclusive groups like memorial-merchandise
        // group visually but allow independent selection of each service.
        const groupMeta = service?.group
          ? groups.find((g) => g.slug === service.group)
          : null;
        if (groupMeta?.exclusive) {
          const groupServiceIds = addOns
            .filter((s) => s.group === service?.group)
            .map((s) => s.id);
          others = others.filter((a) => !groupServiceIds.includes(a.serviceId));
        }
        const updated: AddOnAnswer[] =
          quantity > 0 ? [...others, { serviceId, quantity }] : others;
        return { ...prev, addOnSelections: updated };
      });
    },
    [],
  );

  const clearAddOnSelection = useCallback((serviceId: string) => {
    setState((prev) => ({
      ...prev,
      addOnSelections: prev.addOnSelections.filter(
        (a) => a.serviceId !== serviceId,
      ),
    }));
  }, []);

  const setIsDeceased = useCallback((value: boolean) => {
    setState((prev) => ({ ...prev, isDeceased: value }));
  }, []);

  const setQuoteEmails = useCallback((emails: string[]) => {
    setState((prev) => ({ ...prev, quoteEmails: emails }));
  }, []);

  const setPaymentMethodId = useCallback((id: string) => {
    setState((prev) => ({ ...prev, paymentMethodId: id }));
  }, []);

  // Persist a state now rather than on the autosave's timer. Rejects when the
  // save does, so callers pairing this with an API call of their own can refuse
  // to advance on a draft that never landed.
  const flushState = useCallback(
    async (persistableState: ArrangementState): Promise<void> => {
      // Cancel pending debounce timer so it doesn't fire after our flush
      debouncedSave.cancel();
      // Wait for any in-flight save to complete
      const inFlight = inFlightSaveRef.current;
      if (inFlight) {
        await inFlight.promise.catch(() => {});
      }
      await persistSelections(persistableState);
    },
    [debouncedSave, persistSelections],
  );

  // Flush pending save — ensures Firestore has latest state before operations
  // that read from it (e.g. fingerprint check before signing).
  const flushSave = useCallback(async () => {
    await flushState(stateRef.current);
  }, [flushState]);

  const setServiceAnswersAndFlush = useCallback(
    async (answers: ServiceAnswer[]) => {
      // React commits state a render later, so neither the flush nor the caller
      // can read the merged answers back off state: merge here, persist the
      // object we set, and hand the same answers back.
      const base = stateRef.current;
      const next = mergeServiceAnswers(base, answers);
      stateRef.current = next;
      // An update queued earlier in the same commit (a sibling effect writing a
      // default) has not reached `base` yet, so merge onto whatever React
      // actually holds and let the autosave carry the difference.
      setState((prev) =>
        prev === base ? next : mergeServiceAnswers(prev, answers),
      );
      await flushState(next);
      return next.serviceAnswers;
    },
    [flushState],
  );

  const basePath = caseId ? `/cases/${caseId}/arrange` : "/arrange";
  const basePathRef = useRef(basePath);
  basePathRef.current = basePath;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const goToStep = useCallback(
    (step: number, section?: PackageSection) => {
      if (step >= 0 && step < STEP_ROUTES.length) {
        posthogRef.current?.capture("arrangement_step_navigated", {
          case_id: caseId,
          step: STEP_ROUTES[step],
        });
        navigateRef.current(
          `${basePathRef.current}/${STEP_ROUTES[step]}${searchParams.current}`,
          section ? { state: { section } } : undefined,
        );
      }
    },
    [caseId],
  );

  // Urn/keepsake total (always client-side, not in quote engine)
  // Uses variant-level pricing when available
  const urnKeepsakeTotal = useMemo(() => {
    if (!config) return 0;
    let total = 0;
    // Engraving on included urn
    if (state.includedUrnEngraving) {
      total += ENGRAVING_PRICE;
    }
    for (const sel of state.additionalUrns) {
      const urn = config.urns.find((u) => u.id === sel.urnId);
      if (urn) {
        const variant = urn.variants.find((v) => v.id === sel.variantId);
        total += (variant?.price ?? urn.price) * sel.quantity;
        if (sel.engraving) {
          total += ENGRAVING_PRICE * sel.quantity;
        }
      }
    }
    for (const sel of state.selectedKeepsakes) {
      const keepsake = config.keepsakes.find((k) => k.id === sel.productId);
      if (keepsake) {
        const variant = keepsake.variants?.find((v) => v.id === sel.variantId);
        total += (variant?.price ?? keepsake.price) * sel.quantity;
      }
    }
    return total;
  }, [
    config,
    state.includedUrnEngraving,
    state.additionalUrns,
    state.selectedKeepsakes,
  ]);

  const computedTotal = useMemo(() => {
    if (!config) return 0;

    let total = 0;
    const inclusions = packageInclusions;

    const pkg = config.packages.find(
      (p) => p.variant_id === state.selectedPackageId,
    );
    if (pkg) total += parseFloat(pkg.price);

    total += urnKeepsakeTotal;

    for (const answer of chargeableServiceAnswers(state.serviceAnswers)) {
      const question = config.service_questions.find(
        (q) => q.id === answer.questionId,
      );
      if (question) {
        total += computeServiceAnswerCost(answer, question, inclusions);
      }
    }

    for (const sel of state.addOnSelections) {
      const service = config.add_ons.find((s) => s.id === sel.serviceId);
      if (service && !service.informationalOnly) {
        const includedQty = getIncludedOfferingQuantity(
          inclusions,
          service.offeringId,
        );
        const chargeableQty = Math.max(0, sel.quantity - includedQty);
        total += service.pricing.amount * chargeableQty;
      }
    }

    return total;
  }, [
    config,
    packageInclusions,
    urnKeepsakeTotal,
    state.selectedPackageId,
    state.serviceAnswers,
    state.addOnSelections,
  ]);

  // quoteTotal: server quote + client-side urn/keepsake; fall back to computedTotal
  const quoteTotal = quoteBreakdown
    ? quoteBreakdown.total + urnKeepsakeTotal
    : computedTotal;

  // Memoize the merged state separately so that toggling isSaving
  // (or other outer fields) does not create a new state object reference.
  const mergedState = useMemo(
    () => ({ ...state, currentStep }),
    [state, currentStep],
  );

  // B1: Split into state and actions values
  const stateValue: ArrangementStateContextType = useMemo(
    () => ({
      state: mergedState,
      config,
      viewer,
      purchaser,
      isViewerPurchaser,
      isLoading,
      hasInitialized,
      isSaving,
      isQuoteLoading,
      isReadOnly: isCareSpecialist,
      error,
      packageInclusions,
      quoteBreakdown,
      quoteTotal,
      computedTotal,
      urnKeepsakeTotal,
      stepStatus,
    }),
    [
      mergedState,
      config,
      viewer,
      purchaser,
      isViewerPurchaser,
      isLoading,
      hasInitialized,
      isSaving,
      isQuoteLoading,
      isCareSpecialist,
      error,
      packageInclusions,
      quoteBreakdown,
      quoteTotal,
      computedTotal,
      urnKeepsakeTotal,
      stepStatus,
    ],
  );

  // B1/B2: Actions value is stable because all callbacks use refs
  const actionsValue: ArrangementActionsContextType = useMemo(
    () => ({
      setSelectedPackage,
      setIncludedUrn,
      clearIncludedUrn,
      setIncludedUrnEngraving,
      setAdditionalUrn,
      clearAdditionalUrn,
      setKeepsakeSelection,
      clearKeepsakeSelection,
      updateContactInfo,
      updateFulfillment,
      updateFulfillmentAddress,
      setServiceAnswer,
      setServiceAnswersAndFlush,
      setAddOnSelection,
      clearAddOnSelection,
      setIsDeceased,
      setQuoteEmails,
      setPaymentMethodId,
      flushSave,
      goToStep,
      reportStepStatus,
    }),
    [
      setSelectedPackage,
      setIncludedUrn,
      clearIncludedUrn,
      setIncludedUrnEngraving,
      setAdditionalUrn,
      clearAdditionalUrn,
      setKeepsakeSelection,
      clearKeepsakeSelection,
      updateContactInfo,
      updateFulfillment,
      updateFulfillmentAddress,
      setServiceAnswer,
      setServiceAnswersAndFlush,
      setAddOnSelection,
      clearAddOnSelection,
      setIsDeceased,
      setQuoteEmails,
      setPaymentMethodId,
      flushSave,
      goToStep,
      reportStepStatus,
    ],
  );

  return (
    <ArrangementStateContext.Provider value={stateValue}>
      <ArrangementActionsContext.Provider value={actionsValue}>
        {children}
      </ArrangementActionsContext.Provider>
    </ArrangementStateContext.Provider>
  );
};

/** Read-only state from the arrangement context. */
export const useArrangementState = (): ArrangementStateContextType => {
  const context = useContext(ArrangementStateContext);
  if (!context) {
    throw new Error(
      "useArrangementState must be used within an ArrangementProvider",
    );
  }
  return context;
};

/** Stable action callbacks from the arrangement context. */
export const useArrangementActions = (): ArrangementActionsContextType => {
  const context = useContext(ArrangementActionsContext);
  if (!context) {
    throw new Error(
      "useArrangementActions must be used within an ArrangementProvider",
    );
  }
  return context;
};

/** Combined hook — backward compatible, returns both state and actions. */
export const useArrangement = (): ArrangementContextType => {
  const stateCtx = useArrangementState();
  const actionsCtx = useArrangementActions();
  return { ...stateCtx, ...actionsCtx };
};
